<template>
  <div
    :key="enquiryReasonSelected"
    class="w-full pt-8 px-8 flex flex-col justify-center"
  >
    <h1 class="text-cw-900">Enquire about Free Trial</h1>
    <h6 class="mb-8 text-sm">
      If you are a tertiary/PTE, employer, or industry
      <a class="hover:text-cw-alt" @click="showOrgRegistrationModal">
        click here
      </a>
    </h6>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div class="space-y-6 w-full">
        <InputDropdown
          :selected-options-initial="[enquiryReasonSelected]"
          :label="'Enquiry Type'"
          :options="enquiryReasons"
          :margin-bottom="false"
          :border-enabled="true"
          :shadow-enabled="false"
          :response-selection-id="'new_reason'"
        />

        <!-- Name -->
        <div v-if="!isUpgrading" :class="inputWrapperClass" class="h-12">
          <h6 :class="inputLabelClass">
            Your Name <span class="italic">required</span>
          </h6>
          <input
            id="name"
            v-model="name"
            type="text"
            name="name"
            autocomplete="name"
            :class="inputTextClass"
            @keyup="checkValidation"
            @change="checkValidation"
          />
        </div>
        <!-- /Name -->

        <!-- Email -->
        <div :class="inputWrapperClass" class="h-12">
          <h6 :class="inputLabelClass">
            Contact Email <span class="italic">required</span>
          </h6>
          <input
            id="email"
            v-model="email"
            type="email"
            name="email"
            autocomplete="email"
            :class="inputTextClass"
            @keyup="checkValidation"
            @change="checkValidation"
          />
        </div>
        <!-- /Email -->
      </div>

      <div class="space-y-6 w-full">
        <!-- School -->
        <div :class="inputWrapperClass" class="h-12">
          <h6 :class="inputLabelClass">
            Your School <span class="italic">required</span>
          </h6>
          <input
            id="organisation"
            v-model="organisation"
            type="text"
            name="organisation"
            autocomplete="organisation"
            :class="inputTextClass"
            @keyup="checkValidation"
            @change="checkValidation"
          />
        </div>
        <!-- /School -->

        <!-- Phone number -->
        <div
          v-if="!isUpgrading && !isHail"
          :class="inputWrapperClass"
          class="h-12"
        >
          <h6 :class="inputLabelClass">
            Your Phone Number <span class="italic">required</span>
          </h6>
          <input
            id="phone"
            v-model="phone"
            type="text"
            name="phone"
            autocomplete="phone"
            :class="inputTextClass"
            @keyup="checkValidation"
            @change="checkValidation"
          />
        </div>
        <!-- /Phone number -->

        <!-- Roll -->
        <div
          v-if="!isUpgrading && !isHail"
          :class="inputWrapperClass"
          class="h-12"
        >
          <h6 :class="inputLabelClass">
            Number of students (years 9-13) <span class="italic">required</span>
          </h6>
          <input
            id="schoolRoll"
            v-model="schoolRoll"
            type="number"
            min="0"
            name="schoolRoll"
            autocomplete="schoolRoll"
            :class="inputTextClass"
            @keyup="checkValidation"
            @change="checkValidation"
          />
        </div>
        <!-- /Roll -->
      </div>
    </div>
    <div class="grid grid-cols-1 mt-6">
      <!--
        <a
        v-if="isInfo"
        class="text-truegray-500 hover:text-cw-alt my-4 text-sm"
        target="_blank"
        href="/img/informationPack.pdf"
        >Download information pack</a
        > 
      -->

      <!-- Request Time -->
      <div
        v-if="isDemo || isRegistering"
        :class="inputWrapperClass"
        class="h-24 w-full mb-6"
      >
        <h6 v-if="isDemo" :class="inputLabelClass">
          Which days / times suit you best for a demo?
        </h6>
        <h6 v-else :class="inputLabelClass">
          Which days / times suit you best for onboarding?
        </h6>
        <textarea
          id="requestTime"
          v-model="requestTime"
          type="textarea"
          name="requestTime"
          autocomplete="requestTime"
          rows="2"
          :class="inputTextAreaClass"
          @keyup="checkValidation"
          @change="checkValidation"
        />
      </div>
      <!-- /Request Time -->

      <!-- Comments -->
      <div :class="inputWrapperClass" class="h-24 w-full">
        <h6 v-if="isInfo" :class="inputLabelClass">
          What can we help you with?
        </h6>
        <h6 v-if="isDemo || isRegistering" :class="inputLabelClass">
          Extra comments
        </h6>
        <h6 v-if="isUpgrading" :class="inputLabelClass">
          Extra details <span class="italic">optional</span>
        </h6>
        <h6 v-if="isOther || isHail" :class="inputLabelClass">Enquiry</h6>
        <textarea
          id="comments"
          v-model="comments"
          type="textarea"
          name="comments"
          autocomplete="comments"
          rows="2"
          :class="inputTextAreaClass"
          @keyup="checkValidation"
          @change="checkValidation"
        />
      </div>
      <!-- /Comments -->
      <div class="w-full flex items-center justify-center mt-10">
        <button
          :disabled="!canSubmit || thinking"
          :class="[
            !canSubmit ? 'opacity-25 cursor-not-allowed' : '',
            thinking ? '' : '',
          ]"
          class="btn btn-cw-alt relative w-40 m-0"
          @click="signUp"
        >
          <div class="flex justify-center items-center w-full h-full">
            <div
              class="absolute inset-0 w-full flex justify-center items-center z-20"
            >
              <lottie v-if="thinking" :options="loadingAnimationOptions" />
            </div>
            <div
              :class="[thinking ? 'invisible' : '']"
              class="w-full flex items-center justify-center"
            >
              Submit
            </div>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { shared } from '../../js/mixins/shared.js'
import { mapGetters } from 'vuex'
import { EventBus } from '../../js/plugins/event-bus.js'
import InputDropdown from '../../shared/components/InputDropdown.vue'
import * as loadingAnimation from '../../shared/components/loading.json'

export default {
  components: {
    InputDropdown,
  },

  mixins: [shared],

  props: {
    config: { default: null },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,
      thinking: false,

      canSubmit: false,

      organisation: '',
      schoolRoll: 0,

      name: '',
      email: '',
      phone: '',
      comments: '',

      requestTime: '',

      signUpheight: 0,

      currentYear: new Date().getFullYear(),

      enquiryReason: 'Information',
      enquiryReasonSelected: 0,
      enquiryReasons: [
        { text: 'Information', value: 0 },
        { text: 'Request a demo', value: 1 },
        { text: 'Sign up', value: 2 },
        { text: 'Upgrade plan', value: 3 },
        { text: 'Other', value: 4 },
        { text: 'Hail enquiry', value: 5 },
      ],

      inputWrapperClass:
        'rounded relative bg-white border border-truegray-300 ',
      inputLabelClass:
        'absolute transform duration-100 ease-linear px-1 left-3 top-1 z-40 text-truegray-700',
      inputTextClass:
        'flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300',
      inputTextAreaClass:
        'flex-grow px-4 mr-0 mt-5 outline-none focus:outline-none bg-transparent h-18 font-primary text-black  focus:placeholder-truegray-300 transition-all duration-100 ease-in-out w-full',
      btnClass:
        'bg-black border-none flex justify-center items-center font-primary font-bold py-0',
    }
  },

  computed: {
    ...mapGetters({
      settings: 'shared/getSettings',
      user: 'shared/getUser',
    }),

    isInfo() {
      return this.enquiryReasonSelected === 0
    },

    isDemo() {
      return this.enquiryReasonSelected === 1
    },

    isRegistering() {
      return this.enquiryReasonSelected === 2
    },

    isUpgrading() {
      return this.enquiryReasonSelected === 3
    },

    isOther() {
      return this.enquiryReasonSelected === 4
    },

    isHail() {
      return this.enquiryReasonSelected === 5
    },
  },

  watch: {
    email: {
      handler: function (val) {
        // Remove all white space from inputs...
        val = val.replace(/\s+/g, '') // or you can use your own preferred way to remove leading spaces
        this.$set(this, 'email', val)
      },

      deep: true,
    },

    enquiryReason: {
      handler: function () {
        this.$set(this, 'requestTime', '')
        this.$set(this, 'comments', '')
        if ((this.isUpgrading || this.isHail) && this.user) {
          this.$set(this, 'email', this.user.email || '')
          this.$set(this, 'organisation', this.settings?.profile_title || '')
        }

        this.checkValidation()
      },

      deep: true,
    },
  },

  mounted() {
    if (this.config?.reason) {
      const reason = this.config.reason
      this.$set(this, 'enquiryReason', this.enquiryReasons[reason].text)
      this.$set(
        this,
        'enquiryReasonSelected',
        this.enquiryReasons[reason].value,
      )
    }

    EventBus.$on('new_reason', (selected) => {
      this.$set(this, 'enquiryReason', this.enquiryReasons[selected].text)
      this.$set(
        this,
        'enquiryReasonSelected',
        this.enquiryReasons[selected].value,
      )
    })
  },

  methods: {
    signUp() {
      // Disable the sign in button so that we do not interupt or clog up the call...
      this.$set(this, 'thinking', true)
      // Submit the access auth and kill the preloader...
      axios
        .post('/api/cw-registration', {
          organisation: this.organisation,
          schoolRoll: this.schoolRoll,
          name: this.name,
          email: this.email,
          comments: this.comments,
          phone: this.phone,
          time: this.requestTime,
          type: this.enquiryReason,
        })
        .then((response) => {
          this.$set(this, 'thinking', false)
          // this.$set(this,'canSubmit',true);

          let returned = response.data

          if (returned.success) {
            EventBus.$emit('triggerAlertResponse', {
              status: 'success',
              message:
                'Your email has been sent. The team will be in touch soon!',
            })
          } else {
            EventBus.$emit('triggerAlertResponse', {
              status: 'error',
              message: 'Something went wrong!',
            })
          }
          this.organisation = ''
          this.email = ''
          this.comments = ''
          this.schoolRoll = 0
          this.name = ''
          this.phone = ''
          this.requestTime = ''
        })
    },

    showOrgRegistrationModal() {
      EventBus.$emit('showModal', {
        action: 'cw-organisation-registration',
      })
    },

    /**
     * Validate form field inputs (length of inputs)
     * Upgrading: check email and org name
     * Default: check email, org name, contact name, school roll, and phone
     * Demo/sign up: also check request time
     * Information/other: also check comments
     */
    checkValidation() {
      const textAreaLength = 3
      let canSubmit = false

      // upgrading
      if (this.isUpgrading || this.isHail) {
        canSubmit = this.email && this.organisation?.length >= textAreaLength

        // default if not upgrading
      } else {
        canSubmit =
          this.email &&
          this.organisation?.length >= 3 &&
          this.name?.length >= 2 &&
          this.schoolRoll >= 1 &&
          this.phone?.length >= 6
      }

      // demo or signing up
      if (this.isDemo || this.isRegistering) {
        canSubmit = canSubmit && this.requestTime?.length >= textAreaLength

        // info request or other
      } else if (this.isInfo || this.isOther) {
        canSubmit = canSubmit && this.comments?.length >= textAreaLength
      }

      this.$set(this, 'canSubmit', canSubmit)
    },
  },
}
</script>
